import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select/src/tom-select.popular'
import { TomInput, TomOption } from 'tom-select/src/types/index'
import removeButton from 'tom-select/src/plugins/remove_button/plugin'
import { escape_html } from 'tom-select/src/utils'

TomSelect.define('remove_button', removeButton)

const searchIcon =
  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" class="search-icon">' +
  '<path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"></path>' +
  '</svg>'

interface singleSelectTomOption extends TomOption {
  text: string
  selectedTitle: string
  itemType: string
}

export default class extends Controller {
  static targets = ['select', 'create']
  static values = {
    create: Boolean,
    icon: Boolean,
    hidePlaceholder: Boolean
  }

  declare createValue: boolean
  declare iconValue: boolean

  declare readonly hasSelectTarget: boolean
  declare readonly selectTarget: HTMLElement

  plugin: TomSelect

  connect (): void {
    let selectElement = this.element

    if (this.hasSelectTarget) {
      selectElement = this.selectTarget
    }

    this.plugin = new TomSelect(
      selectElement as TomInput, {
        plugins: ['remove_button'],
        hidePlaceholder: true,
        maxOptions: 99999,
        create: this.createValue,
        render: {
          item: this.formatItem
        },
        onItemRemove: (value: string) => this.triggerItemChange(value, 'remove'),
        onOptionAdd: (value: string) => this.triggerItemChange(value, 'add-option'),
        onItemAdd: (value: string, item: HTMLElement) => {
          this.plugin.setTextboxValue('')
          this.triggerItemChange(value, 'add')
        }
      }
    )

    this.plugin.on('focus', () => setTimeout(() => this.adjustDropdownPosition(), 0))
    this.plugin.on('type', () => this.adjustDropdownPosition())

    if (this.iconValue) {
      this.plugin.control.insertAdjacentHTML('beforeend', searchIcon)
    }
  }

  triggerItemChange (value: string, action: string): void {
    this.dispatch(action, { bubbles: true, detail: { value } })
  }

  markAsTouched (element: Element): void {
    this.getWrapperClassList().add('changed')
  }

  resetState (): void {
    this.plugin.clear(true)
    this.getWrapperClassList().remove('changed')
  }

  updateOptions ({ detail: { values } }: { detail: { values: any[] } }): void {
    const options: TomOption[] = values.map(value => ({ value: value.id, text: value.name }))

    this.plugin.clear(true)
    this.plugin.clearOptions()
    this.plugin.addOptions(options)
  }

  filterAvailableOptions ({ detail: { itemType } }: { detail: { itemType: string } }): void {
    this.plugin.clear(true)
    this.plugin.sync(true)

    Object.keys(this.plugin.options)
      .filter((key) => this.plugin.options[key].itemType !== itemType)
      .forEach((key) => this.plugin.removeOption(key))
  }

  getWrapperClassList (): DOMTokenList {
    return this.plugin.wrapper.classList
  }

  adjustDropdownPosition (): void {
    setTimeout(() => {
      const dropdown = this.plugin.dropdown
      const inputRect = this.plugin.control_input.getBoundingClientRect()
      const spaceBelow = window.innerHeight - inputRect.bottom

      dropdown.style.top = (spaceBelow > 260) ? '' : `-${dropdown.clientHeight + 10}px`
    }, 0)
  }

  formatItem (data: singleSelectTomOption, escape: typeof escape_html): string {
    const text = data.selectedTitle !== undefined ? data.selectedTitle : data.text

    return `<div><p>${escape(text)}</p></div>`
  }
}
